import React, { useState, useMemo } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './CustomCalendar.scss'; // Importa los estilos SCSS

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { disabledDate } from '../../utils/functionTurns';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

export default function CustomCalendar({
  onDaySelect,
  scheduleConfig,
  tenantTimezone,
  configSchedule,
  allTurns,
  holidays = [], // Recibir holidays como prop con valor por defecto
  selectedBranchId = null,
  selectedServiceId = null,
  liveScheduleHours = null,
  calendarWeekSchedule = null,
}) {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDay, setSelectedDay] = useState(dayjs());

  const handleDayClick = (day) => {
    setSelectedDay(day);
    onDaySelect(day);
  };

  // Map de feriados/holidays desde la API
  const holidaysMap = useMemo(() => {
    const map = new Map();
    holidays.forEach((holiday) => {
      // La API devuelve { year, month, day, ... }
      const key = `${holiday.year}-${holiday.month}-${holiday.day}`;
      map.set(key, true);
    });
    return map;
  }, [holidays]);

  // Map de días excluidos
  const excludedDaysMap = useMemo(() => {
    const map = new Map();
    scheduleConfig?.excludeDays
      ?.filter((day) => !dayjs(day).isBefore(dayjs().startOf('day'))) // Filtra solo días futuros o presentes
      ?.forEach((day) => {
        const formattedDay = dayjs(day)
          .tz(tenantTimezone)
          .startOf('day')
          .toISOString(); // Convertir al mismo formato de comparación
        map.set(formattedDay, true);
      });
    return map;
  }, [scheduleConfig?.excludeDays, tenantTimezone]);

  // Funciones para cambiar el mes
  const goToPreviousMonth = () => {
    setCurrentDate((prevDate) => prevDate.clone().subtract(1, 'month'));
    setSelectedDay(null); // Reinicia la selección al cambiar de mes
  };

  const goToNextMonth = () => {
    setCurrentDate((prevDate) => prevDate.clone().add(1, 'month'));
    setSelectedDay(null); // Reinicia la selección al cambiar de mes
  };

  return (
    <div className="custom-calendar">
      {/* Header del calendario */}
      <div className="calendar-header">
        <LeftOutlined className="nav-arrow" onClick={goToPreviousMonth} />
        <h3 className="month-label">{currentDate.format('MMMM YYYY')}</h3>
        <RightOutlined className="nav-arrow" onClick={goToNextMonth} />
      </div>

      {/* Días de la semana */}
      <div className="weekdays">
        {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-grid">
        {[
          ...Array(currentDate.startOf('month').day()), // Espacios vacíos para los días antes del primer día del mes
          ...Array(currentDate.daysInMonth()), // Días del mes
        ].map((_, i) => {
          const day = currentDate
            .startOf('month')
            .add(i - currentDate.startOf('month').day(), 'day');

          // Comprobar si el día está deshabilitado
          const { isDisabled, freeSlots } =
            i < currentDate.startOf('month').day() ||
            disabledDate({
              current: day,
              excludedDaysMap,
              holidaysMap,
              configSchedule,
              scheduleConfig,
              allTurns,
              selectedBranchId,
              selectedServiceId,
              liveScheduleHours,
              calendarWeekSchedule,
            });
            
          return (
            <div
              key={i}
              className={`calendar-day ${isDisabled ? 'disabled' : ''} ${
                selectedDay && day.isSame(selectedDay, 'day') ? 'selected' : ''
              }`}
              onClick={() => !isDisabled && handleDayClick(day)}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                backgroundColor:
                  isDisabled || isDisabled === undefined
                    ? '#cccccc'
                    : selectedDay && day.isSame(selectedDay, 'day')
                    ? '#1890ff'
                    : freeSlots <= 0
                    ? 'rgba(255, 0, 0, 0.3)' // Rojo con opacidad
                    : 'rgba(0, 128, 0, 0.3)', // Verde
                color: isDisabled ? '#aaa' : '',
              }}
            >
              {i >= currentDate.startOf('month').day() ? day.date() : null}
            </div>
          );
        })}
      </div>
      {/* Leyenda de colores */}
      <div className="calendar-legend">
        <span className="legend-box legend-green"></span> Disponible
        <span style={{ marginLeft: 16 }}></span>
        <span className="legend-box legend-red"></span> No Disponible
      </div>
    </div>
  );
}
